import {
  AgreementState,
  BillingFrequency,
  ConsistencyIssue,
  ContractType,
  Phase,
  PricingMode,
  RecurrenceType,
  ServiceType,
  TemplateType,
  UnitType,
} from '../types/agreement'
import { validateMilestoneSchedule } from '../lib/milestoneBillingMath'

/**
 * Map a `service_type` to the set of `template_type`s that make sense for it.
 * `custom` template is always allowed as an escape hatch.
 * When `serviceType` is empty/unknown, all templates are allowed.
 */
export function getCompatibleTemplateTypes(
  serviceType: ServiceType | string | undefined | null
): TemplateType[] {
  const all: TemplateType[] = ['maintenance', 'cleaning', 'landscaping', 'security', 'dsny', 'custom']
  switch (serviceType) {
    case 'maintenance':
    case 'maintenance_prevailing_wage':
      return ['maintenance', 'custom']
    case 'dsny':
      return ['dsny', 'maintenance', 'custom']
    case 'maintenance_dsny':
    case 'dsny_prevailing_wage':
      return ['maintenance', 'dsny', 'custom']
    case 'cleaning':
    case 'construction_cleaning':
    case 'apartment_turnovers':
    case 'office_cleaning':
    case 'hourly_clean':
      return ['cleaning', 'custom']
    case 'landscaping':
      return ['landscaping', 'custom']
    case 'security':
      return ['security', 'custom']
    case 'custom':
      return all
    default:
      return all
  }
}

/** Default template_type when user picks a service_type on proposal/contract forms */
export function getDefaultTemplateTypeForServiceType(
  serviceType: ServiceType | string | undefined | null
): TemplateType | '' {
  switch (serviceType) {
    case 'maintenance':
    case 'maintenance_prevailing_wage':
    case 'maintenance_dsny':
    case 'dsny_prevailing_wage':
      return 'maintenance'
    case 'cleaning':
    case 'construction_cleaning':
    case 'apartment_turnovers':
    case 'office_cleaning':
    case 'hourly_clean':
      return 'cleaning'
    case 'landscaping':
      return 'landscaping'
    case 'security':
      return 'security'
    case 'custom':
      return 'custom'
    default:
      return ''
  }
}

/** Default job type (contract_type) inferred from service_type */
export function getDefaultContractTypeForServiceType(
  serviceType: ServiceType | string | undefined | null
): ContractType | '' {
  if (!serviceType) return ''
  if (serviceType === 'custom') return ''
  // Turnovers are ongoing/as-needed; other cleaning-style is often one-time; maintenance/DSNY recur.
  switch (serviceType) {
    case 'apartment_turnovers':
      return 'as_needed'
    case 'construction_cleaning':
    case 'hourly_clean':
      return 'one_time'
    default:
      return 'recurring'
  }
}

/**
 * Filter the unit_type dropdown based on contract_type.
 * Section A of the decision matrix.
 */
export function getValidUnitTypes(contractType: ContractType): UnitType[] {
  if (contractType === 'one_time' || contractType === 'as_needed') {
    // A1, A2 hide per_month and per_visit; include rate-card unit types for turnovers/hourly
    return [
      'per_hour',
      'per_sqft',
      'per_unit',
      'per_apartment',
      'per_bedroom',
      'per_bathroom',
      'flat_rate',
    ]
  }
  return ['per_month', 'per_visit', 'per_hour', 'per_sqft', 'per_unit', 'flat_rate']
}

/**
 * Filter the billing_frequency dropdown based on contract_type.
 * Section C of the decision matrix.
 */
export function getValidBillingFrequencies(contractType: ContractType): BillingFrequency[] {
  if (contractType === 'one_time' || contractType === 'as_needed') {
    // Usage-style: one_time, after_each_service, and progress
    return ['one_time', 'after_each_service', 'progress']
  }
  // C1: ALLOW one_time even on recurring contracts (annual upfront use case)
  return ['one_time', 'weekly', 'bi_weekly', 'monthly', 'quarterly', 'annually', 'after_each_service']
}

/**
 * Filter recurrence dropdown options.
 * 'daily' is allowed in both phases (B6 fix).
 */
export function getValidRecurrenceTypes(
  contractType: ContractType,
  phase: Phase
): RecurrenceType[] {
  if (contractType === 'one_time' || contractType === 'as_needed') {
    return ['one_time']
  }
  // recurring contracts can be daily/weekly/bi_weekly/monthly/quarterly/annually/custom_interval
  if (phase === 'proposal') {
    // proposals capture frequency, not specific days
    return ['daily', 'weekly', 'bi_weekly', 'monthly', 'quarterly', 'annually', 'custom_interval']
  }
  return ['daily', 'weekly', 'bi_weekly', 'monthly', 'quarterly', 'annually', 'custom_interval']
}

/**
 * Main consistency checker. Returns a list of issues with level + target nav.
 */
export function checkConsistency(state: AgreementState, phase: Phase): ConsistencyIssue[] {
  const issues: ConsistencyIssue[] = []
  const isRecurring = state.contract_type === 'recurring'
  const isOneTime =
    state.contract_type === 'one_time' || state.contract_type === 'as_needed'
  const isDsny =
    state.service_type === 'maintenance_dsny' ||
    state.service_type === 'dsny' ||
    state.service_type === 'dsny_prevailing_wage' ||
    Boolean(state.dsny_integration)

  // Building Upkeep / Exterior packages schedule via visit groups or DSNY day
  // arrays — require real cadence (days, or daily/monthly), not a bare type.
  const hasDsnyScheduleCoverage =
    (state.visit_groups || []).some((vg) => {
      const rtype = vg.recurrence_type
      if (!rtype || rtype === 'none') return false
      if (rtype === 'daily' || rtype === 'monthly') return true
      return Array.isArray(vg.recurrence_days) && vg.recurrence_days.length > 0
    }) ||
    (state.garbage_schedule || []).length > 0 ||
    (state.recycling_schedule || []).length > 0 ||
    (state.organics_schedule || []).length > 0 ||
    (state.bulk_schedule || []).length > 0

  // Package billing lines are covered when the schedule UI (DSNY or visit groups)
  // has real days — including non-DSNY maintenance that still uses "Building Upkeep".
  const isDsnyPackageLineCovered = (line: { description?: string; recurrence_type?: string | null }) =>
    hasDsnyScheduleCoverage &&
    ((line.description || '').toLowerCase().includes('building upkeep') ||
      (line.description || '').toLowerCase().includes('exterior maintenance'))

  // The consistency panel is for CROSS-FIELD issues (e.g., one-time contract
  // with a per-month line item). Plain "this required field is empty" cases
  // are handled by the `*` markers and HTML `required` on each input, plus the
  // submit handler's own checks. Until the user picks a contract_type we have
  // no way to evaluate cross-field rules, so just return an empty list.
  if (!state.contract_type) {
    return issues
  }

  // ===== Section A: contract_type vs unit_type =====
  for (const line of state.line_items || []) {
    const unit = (line.unit_type || '').toLowerCase()
    // A1, A2: BLOCK one_time + per_month / per_visit
    if (isOneTime && (unit === 'per_month' || unit === 'per_visit')) {
      issues.push({
        code: 'A1_A2',
        level: 'block',
        message: `Line "${line.description || 'untitled'}" uses ${unit.replace('_', ' ')} pricing, which only makes sense on a recurring contract.`,
        target: { tab: 'services', anchor: `line-item-${line.id}`, field: 'unit_type' },
      })
    }
    // A7: WARN recurring + flat_rate (skip DSNY billing packages covered by visit groups)
    if (
      isRecurring &&
      unit === 'flat_rate' &&
      !line.recurrence_type &&
      !isDsnyPackageLineCovered(line)
    ) {
      issues.push({
        code: 'A7',
        level: 'warn',
        message: `Line "${line.description || 'untitled'}" is flat-rate; on a recurring service it bills only once.`,
        target: { tab: 'services', anchor: `line-item-${line.id}`, field: 'unit_type' },
      })
    }
  }

  // ===== Section B: recurrence configuration =====
  for (const line of state.line_items || []) {
    // B1: recurring contract + missing recurrence_type
    // DSNY billing packages (Building Upkeep) are scheduled via visit groups / DSNY days.
    if (isRecurring && !line.recurrence_type && !isDsnyPackageLineCovered(line)) {
      issues.push({
        code: 'B1',
        level: 'block',
        message: `Line "${line.description || 'untitled'}" has no recurrence schedule set.`,
        target: { tab: 'services', anchor: `line-item-${line.id}`, field: 'recurrence_type' },
      })
    }
    // B2: proposal phase, recurring contract, missing recurrence_interval
    if (
      phase === 'proposal' &&
      isRecurring &&
      line.recurrence_type &&
      line.recurrence_type !== 'one_time' &&
      (!line.recurrence_interval || line.recurrence_interval <= 0)
    ) {
      issues.push({
        code: 'B2',
        level: 'block',
        message: `Specify how often "${line.description || 'untitled'}" recurs (e.g., 3 days/week).`,
        target: { tab: 'services', anchor: `line-item-${line.id}`, field: 'recurrence_interval' },
      })
    }
    // B3: contract phase, recurring, type weekly/bi_weekly/custom_interval, empty recurrence_days
    if (
      phase === 'contract' &&
      isRecurring &&
      ['weekly', 'bi_weekly', 'custom_interval', 'custom'].includes(String(line.recurrence_type)) &&
      (!line.recurrence_days || line.recurrence_days.length === 0) &&
      !isDsnyPackageLineCovered(line)
    ) {
      issues.push({
        code: 'B3',
        level: 'block',
        message: `Pick the days of the week for "${line.description || 'untitled'}".`,
        target: { tab: 'services', anchor: `line-item-${line.id}`, field: 'recurrence_days' },
      })
    }
    // B5: one_time contract + recurrence_type set
    if (
      isOneTime &&
      line.recurrence_type &&
      line.recurrence_type !== 'one_time' &&
      line.recurrence_type !== ''
    ) {
      issues.push({
        code: 'B5',
        level: 'warn',
        message: `Line "${line.description || 'untitled'}" has a recurring schedule but the contract is one-time. The schedule will be cleared on save.`,
        target: { tab: 'services', anchor: `line-item-${line.id}`, field: 'recurrence_type' },
      })
    }
  }

  // ===== Section C: billing frequency & method =====
  if (isOneTime) {
    const validBf = getValidBillingFrequencies('one_time')
    if (state.billing_frequency && !validBf.includes(state.billing_frequency as BillingFrequency)) {
      issues.push({
        code: 'C2',
        level: 'block',
        message: `Billing frequency "${state.billing_frequency}" only applies to recurring contracts.`,
        target: { tab: 'billing', anchor: 'billing-frequency', field: 'billing_frequency' },
      })
    }
  }
  // C5: actual_usage without any variable items
  if (state.billing_method === 'actual_usage') {
    const hasVariable = (state.line_items || []).some((l) => l.is_variable)
    if (!hasVariable) {
      issues.push({
        code: 'C5',
        level: 'warn',
        message:
          'Billing method is "actual usage" but no line items are marked variable. Mark at least one line item as variable, or change the billing method.',
        target: { tab: 'billing', anchor: 'billing-method', field: 'billing_method' },
      })
    }
  }

  // C7: rate_card presentation should pair with actual_usage billing
  if (state.pricing_mode === 'rate_card' && state.billing_method && state.billing_method !== 'actual_usage') {
    issues.push({
      code: 'C7',
      level: 'warn',
      message:
        'Rate card pricing is best paired with billing method "Actual usage" so jobs bill at the listed per-unit rates.',
      target: { tab: 'billing', anchor: 'billing-method', field: 'billing_method' },
    })
  }

  // C8: rate_card mode requires at least one line item with a rate
  if (state.pricing_mode === 'rate_card') {
    const hasRatedLine = (state.line_items || []).some((l) => (l.unit_price || 0) > 0)
    if (!hasRatedLine) {
      issues.push({
        code: 'C8',
        level: 'warn',
        message: 'Rate card mode requires at least one line item with a unit price set.',
        target: { tab: 'billing', anchor: 'pricing-presentation', field: 'pricing_mode' },
      })
    }
  }

  // C6: progress billing requires valid milestone schedule
  if (state.billing_frequency === 'progress') {
    const milestoneErrors = validateMilestoneSchedule(state.billing_milestones || [])
    if (milestoneErrors.length > 0) {
      issues.push({
        code: 'C6',
        level: 'block',
        message: milestoneErrors[0],
        target: { tab: 'billing', anchor: 'milestone-schedule', field: 'billing_milestones' },
      })
    }
    if (state.billing_method && state.billing_method !== 'progress') {
      issues.push({
        code: 'C6b',
        level: 'warn',
        message: 'Progress billing should use billing method "Progress".',
        target: { tab: 'billing', anchor: 'billing-method', field: 'billing_method' },
      })
    }
  } else if ((state.billing_milestones || []).length > 0) {
    issues.push({
      code: 'C6c',
      level: 'warn',
      message: 'Milestone schedule is set but billing frequency is not Progress.',
      target: { tab: 'billing', anchor: 'milestone-schedule', field: 'billing_frequency' },
    })
  }

  // ===== Section D: DSNY =====
  // D1: DSNY contract + all DSNY rows empty
  if (phase === 'contract' && isDsny) {
    const allEmpty =
      (state.garbage_schedule || []).length === 0 &&
      (state.recycling_schedule || []).length === 0 &&
      (state.organics_schedule || []).length === 0 &&
      (state.bulk_schedule || []).length === 0
    if (allEmpty) {
      issues.push({
        code: 'D1',
        level: 'block',
        message: 'DSNY contract requires at least one pickup day to be set.',
        target: { tab: 'services', anchor: 'dsny-schedule', field: 'dsny_schedule' },
      })
    }
  }
  // D2 / D6: DSNY service + one_time contract
  if (isOneTime && isDsny) {
    issues.push({
      code: 'D2',
      level: 'block',
      message: 'DSNY services are inherently recurring. Change contract type to recurring.',
      target: { tab: 'overview', anchor: 'contract-type', field: 'contract_type' },
    })
  }
  // D5: DSNY + no address
  if (isDsny && !state.property_address) {
    issues.push({
      code: 'D5',
      level: 'warn',
      message: 'Pick a property with an address so DSNY days can be auto-fetched.',
      target: { tab: 'overview', anchor: 'property', field: 'property_id' },
    })
  }

  // ===== Section E: dates =====
  // Dates live on the Billing tab in the create/edit forms, so route warnings there.
  if (state.start_date && state.end_date) {
    const start = new Date(state.start_date)
    const end = new Date(state.end_date)
    if (start > end) {
      issues.push({
        code: 'E1',
        level: 'block',
        message: 'Start date must be before end date.',
        target: { tab: 'billing', anchor: 'contract-dates', field: 'start_date' },
      })
    }
  }
  // Recurring start/end are contract-only; proposals may leave them blank until conversion.
  if (phase === 'contract' && isRecurring && !state.start_date) {
    issues.push({
      code: 'E3',
      level: 'block',
      message: 'Recurring contracts require a start date.',
      target: { tab: 'billing', anchor: 'contract-dates', field: 'start_date' },
    })
  }
  if (phase === 'contract' && isRecurring && !state.end_date) {
    issues.push({
      code: 'E2',
      level: 'block',
      message: 'Recurring contracts require an end date (or set duration in months from a start date).',
      target: { tab: 'billing', anchor: 'contract-dates', field: 'end_date' },
    })
  }
  // One-time contracts: start/end dates are optional (job timing lives on services).

  // ===== Section E5/E6: visit groups (contract phase only) =====
  if (phase === 'contract' && state.visit_groups) {
    for (const vg of state.visit_groups) {
      if (vg.start_date && vg.end_date) {
        const s = new Date(vg.start_date)
        const e = new Date(vg.end_date)
        if (s > e) {
          issues.push({
            code: 'E5',
            level: 'block',
            message: `Visit group "${vg.name || vg.id}" has start date after end date.`,
            target: { tab: 'visit_groups', anchor: `visit-group-${vg.id}`, field: 'start_date' },
          })
        }
      }
      if (state.end_date && vg.end_date) {
        if (new Date(vg.end_date) > new Date(state.end_date)) {
          issues.push({
            code: 'E6',
            level: 'warn',
            message: `Visit group "${vg.name || vg.id}" ends after the contract end date.`,
            target: { tab: 'visit_groups', anchor: `visit-group-${vg.id}`, field: 'end_date' },
          })
        }
      }
    }
  }

  // ===== Section F: variable line items =====
  for (const line of state.line_items || []) {
    if (line.is_variable && (!line.unit_price || line.unit_price === 0)) {
      issues.push({
        code: 'F1',
        level: 'warn',
        message: `Variable line "${line.description || 'untitled'}" has $0 unit price.`,
        target: { tab: 'services', anchor: `line-item-${line.id}`, field: 'unit_price' },
      })
    }
    if (line.is_variable && (line.unit_type || '').toLowerCase() === 'flat_rate') {
      issues.push({
        code: 'F2',
        level: 'warn',
        message: `Variable line "${line.description || 'untitled'}" is flat-rate; usually variable lines should be per-hour or per-unit.`,
        target: { tab: 'services', anchor: `line-item-${line.id}`, field: 'unit_type' },
      })
    }
    if (
      !line.is_variable &&
      (['per_hour', 'per_unit', 'per_apartment', 'per_bedroom', 'per_bathroom'] as string[]).includes(
        (line.unit_type || '').toLowerCase()
      )
    ) {
      issues.push({
        code: 'F3',
        level: 'warn',
        message: `Line "${line.description || 'untitled'}" is ${line.unit_type} but is not marked variable.`,
        target: { tab: 'services', anchor: `line-item-${line.id}`, field: 'is_variable' },
      })
    }
  }

  // ===== Section G: service_type vs template_type correlation =====
  // Both fields describe the same domain (maintenance, cleaning, landscaping,
  // security, DSNY) so they should align. `custom` template is always allowed.
  if (state.service_type && state.template_type) {
    const compatible = getCompatibleTemplateTypes(state.service_type)
    if (!compatible.includes(state.template_type as TemplateType)) {
      const serviceLabel = String(state.service_type).replace(/_/g, ' ')
      const templateLabel = String(state.template_type).replace(/_/g, ' ')
      issues.push({
        code: 'G1',
        level: 'warn',
        message: `Service type "${serviceLabel}" doesn't match template type "${templateLabel}". Pick a matching template or set the template to Custom.`,
        target: { tab: 'overview', anchor: 'template-type', field: 'template_type' },
      })
    }
  }

  return issues
}

/**
 * Convenience: separate warnings and blockers.
 */
export function splitIssues(issues: ConsistencyIssue[]): {
  warnings: ConsistencyIssue[]
  blockers: ConsistencyIssue[]
} {
  return {
    warnings: issues.filter((i) => i.level === 'warn'),
    blockers: issues.filter((i) => i.level === 'block'),
  }
}
